import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { apiRequest } from '../../utils/AxiosHelper';
import API from '../../modules/settings/APIs';
import { API_PARAMS } from '../../constants/constants';
import { useAppSelector } from '../hooks';


export interface IssueMonitoringEntry {
  id?: number;
  idIssue?: number;
  stateIncident?: number;
  uid?: string;
  descriptionStateIncident?: string;
  colorStateIncident?: string;
  typeIncident?: string;
  descriptionTrack?: string;
  fullNameUserRegister?: string;
  dateTrack?: string; // backend format: "dd/MM/yyyy HH:mm"
  indicatorAttachment?: string; // "S" | "N"
  hasAttachment?: boolean;
}

export const MONITORING_DATE_FORMAT = 'DD/MM/YYYY HH:mm';

const queryKey = (idIssue?: number | string) => ['issues', 'monitoring', idIssue] as const;


export const useIssueActivity = (idIssue?: number | string) => {
  const { isAuthenticated, token } = useAppSelector((state) => state.session);

  return useQuery({
    queryKey: queryKey(idIssue),
    queryFn: async (): Promise<IssueMonitoringEntry[]> => {
      if (!idIssue) return [];
      const response = await apiRequest.get(API.issues.monitoring.list(idIssue));
      const entries: IssueMonitoringEntry[] = response.data?.data ?? [];
      // Newest first (backend query has no explicit ORDER BY).
      return [...entries].sort((a, b) => {
        const da = dayjs(a.dateTrack, MONITORING_DATE_FORMAT).valueOf() || 0;
        const db = dayjs(b.dateTrack, MONITORING_DATE_FORMAT).valueOf() || 0;
        return db - da;
      });
    },
    enabled: isAuthenticated && !!token && !!idIssue,
    staleTime: 0,
    refetchOnMount: 'always',
  });
};


export interface MonitoringFile {
  key: string;
  name: string;
  type?: string;
  size?: number;
  url?: string;
  presignedUrl?: string;
}


export const useMonitoringAttachments = (
  issueUid?: string,
  entryUid?: string,
  enabled = true,
) => {
  const { isAuthenticated, token } = useAppSelector((state) => state.session);

  return useQuery({
    queryKey: ['issues', 'monitoring', 'attachments', issueUid, entryUid],
    queryFn: async (): Promise<MonitoringFile[]> => {
      if (!issueUid || !entryUid) return [];
      const response = await apiRequest.get(API.issues.file.list, {
        params: {
          uid: issueUid,
          module: API_PARAMS.IMPACT.ISSUES_ENTITY,
          pathSubModule: API_PARAMS.S3.MONITORING_SUBMODULE,
          uuidSubEntity: entryUid,
        },
      });
      return response.data ?? [];
    },
    enabled: enabled && isAuthenticated && !!token && !!issueUid && !!entryUid,
    staleTime: 60 * 1000,
  });
};


export const downloadMonitoringFile = async (file: MonitoringFile): Promise<void> => {
  const response = await apiRequest.get(API.issues.file.download, {
    params: { key: file.key, contentType: file.type ?? '' },
    responseType: 'blob',
  });
  const blobUrl = window.URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

export interface CreateMonitoringInput {
  idIssue: number;
  stateIncident: number;
  issueUid: string; // route/issue UID — used for the S3 attachment path
  descriptionTrack: string;
  files?: File[];
}


export const useCreateMonitoring = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ idIssue, stateIncident, issueUid, descriptionTrack, files }: CreateMonitoringInput) => {
      const uid = crypto.randomUUID();
      const dateTrack = dayjs().format(MONITORING_DATE_FORMAT);

      const response = await apiRequest.post(API.issues.monitoring.save, {
        idIssue,
        stateIncident,
        uid,
        descriptionTrack,
        dateTrack,
      });

      if (files?.length) {
        for (const file of files) {
          const form = new FormData();
          form.append('file', file, file.name);
          await apiRequest.post(API.issues.file.upload, form, {
            params: {
              uid: issueUid,
              module: API_PARAMS.IMPACT.ISSUES_ENTITY,
              pathSubModule: API_PARAMS.S3.MONITORING_SUBMODULE,
              uuidSubEntity: uid,
            },
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }

        await apiRequest.put(API.issues.monitoring.updateAttachmentIndicator, null, {
          params: { uidIssue: issueUid, uidMonitoring: uid },
        });
      }

      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKey(variables.idIssue) });
    },
  });
};
